import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/KeycloakContext';
import { useCredit } from '../../contexts/CreditContext';
import { useNavigate } from 'react-router-dom';
import './Members.css';
import Toast from '../../ui/Toast';
import LeadgenLoading from '../../ui/LeadgenLoading.js';
import ActivityLog from './ActivityLog';
import Pagination from '../../ui/Pagination';
import Banner from '../../ui/Banner';
import axios from '../../api/axios';

/**
 * Members Page Component — Team Management
 */
function Members() {
  const { keycloak } = useAuth();
  const currentUserId = keycloak?.tokenParsed?.sub;
  const {
    workspaceId,
    workspaceName,
    canManageMembers,
    isOrganizationAdmin,
    isOwner,
    isSuperadmin,
    primaryWorkspace,
    refreshWorkspaceContext,
  } = useWorkspace();

  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });
  const [signupLink, setSignupLink] = useState(null); // shown when email can't be sent

  // Section (top-level combined page tabs)
  const [section, setSection] = useState('team'); // 'team' | 'activity-logs'

  // Tabs
  const [activeTab, setActiveTab] = useState('members');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageInput, setPageInput] = useState('1');

  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('org_member');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState(null);

  // Delete modal
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Credit Member Status tab
  const [creditMembers, setCreditMembers] = useState([]);
  const [creditLoading, setCreditLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  // ——————————————————————————————————————————————————————————————————————————
  const fetchMembers = useCallback(async () => {
    if (!workspaceId || !keycloak?.token) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/workspace/${workspaceId}/members`, {
        headers: { Authorization: `Bearer ${keycloak.token}` },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) setMembers(result.members);
      } else {
        const err = await response.json();
        setToast({ show: true, message: err.message || 'Failed to fetch members', type: 'error' });
      }
    } catch (err) {
      setToast({ show: true, message: 'Failed to fetch members', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [workspaceId, keycloak?.token, API_URL]);

  // Fetch credit member status for the organization
  const fetchCreditMembers = useCallback(async () => {
    if (!workspaceId || !keycloak?.token) return;
    try {
      setCreditLoading(true);
      const response = await fetch(`${API_URL}/credits/org/members/status`, {
        headers: { Authorization: `Bearer ${keycloak.token}` },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) setCreditMembers(result.data);
      } else {
        const err = await response.json();
        setToast({ show: true, message: err.message || 'Failed to fetch credit status', type: 'error' });
      }
    } catch (err) {
      setToast({ show: true, message: 'Failed to fetch credit status', type: 'error' });
    } finally {
      setCreditLoading(false);
    }
  }, [workspaceId, keycloak?.token, API_URL]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Auto-clear messages
  // (Toast handles its own auto-clear if we wanted, but here we can just close it if we want)
  // Actually Toast component has its own timer so we don't need these manual ones.

  // Close bulk role menu on outside click

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, statusFilter, itemsPerPage]);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  // —— Invite —————————————————————————————————————————————————————————————————
  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteError(null);
    setSignupLink(null);
    try {
      const response = await fetch(`${API_URL}/workspace/${workspaceId}/members/invite`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setToast({ show: true, message: result.message, type: 'success' });
        setInviteEmail('');
        setInviteRole('org_member');
        setShowInviteModal(false);
        setInviteError(null);
        fetchMembers();
      } else {
        // Special handling for "user not on platform" error
        if (result.code === 'USER_NOT_FOUND') {
          setInviteError(`❌ ${result.message}`);
        } else if (result.code === 'USER_ALREADY_EXISTS') {
          // Show as toast and close modal as requested
          setToast({ show: true, message: result.message, type: 'error' });
          setShowInviteModal(false);
          setInviteError(null);
        } else {
          setInviteError(result.message || 'Failed to invite member');
        }
      }
    } catch {
      setInviteError('Failed to invite member. Please check your connection.');
    } finally {
      setInviting(false);
    }
  };

  // —— Pending role change for confirmation dialog ————————————————————————
  const [roleChangeConfirm, setRoleChangeConfirm] = useState(null);
  // { userId, userName, currentRole, newRole, memberCredit }

  // ——————————————————————————————————————————————————————————————————————————
  const requestRoleChange = (member, newRole) => {
    // Determine if this role change involves credit impact
    const isPromotion = newRole === 'org_admin' || newRole === 'org_owner';
    const isDowngrade = member.workspace_role === 'org_admin' && newRole === 'org_member';
    const previousRoleLabel = member.workspace_role === 'org_admin' ? 'Admin' : 'Member';
    const newRoleLabel =
      newRole === 'org_admin' ? 'Admin' : newRole === 'org_owner' ? 'Owner' : 'Member';

    if (isPromotion || isDowngrade) {
      setRoleChangeConfirm({
        userId: member.user_id,
        userName: member.display_name || member.username || 'Member',
        currentRoleLabel: previousRoleLabel,
        newRoleLabel,
        memberCredit: member.credit_balance || 0,
        newRole,
      });
    } else {
      handleRoleChange(member.user_id, newRole);
    }
  };

  const handleConfirmRoleChange = () => {
    if (!roleChangeConfirm) return;
    handleRoleChange(roleChangeConfirm.userId, roleChangeConfirm.newRole);
    setRoleChangeConfirm(null);
  };

  const handleCancelRoleChange = () => {
    setRoleChangeConfirm(null);
  };

  // ——————————————————————————————————————————————————————————————————————————
  const handleRoleChange = async (userId, newRole) => {
    try {
      const response = await fetch(`${API_URL}/workspace/${workspaceId}/members/${userId}/role`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole }),
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setToast({
          show: true,
          message: result.message || 'Member role updated successfully',
          type: 'success',
        });
        fetchMembers();
        if (refreshWorkspaceContext) refreshWorkspaceContext();
      } else {
        setToast({ show: true, message: result.message || 'Failed to update role', type: 'error' });
      }
    } catch {
      setToast({ show: true, message: 'Failed to update role', type: 'error' });
    }
  };

  // ── Remove ─────────────────────────────────────────────────────────────────
  const handleRemove = (member) => {
    setMemberToRemove(member);
  };

  const confirmRemoveUser = async () => {
    if (!memberToRemove) return;
    setRemoving(true);
    setToast({ ...toast, show: false });
    const userId = memberToRemove.user_id;

    try {
      const response = await fetch(`${API_URL}/workspace/${workspaceId}/members/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${keycloak.token}` },
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setToast({ show: true, message: 'Member removed successfully', type: 'success' });
        fetchMembers();
      } else {
        setToast({
          show: true,
          message: result.message || 'Failed to remove member',
          type: 'error',
        });
      }
    } catch {
      setToast({ show: true, message: 'Failed to remove member', type: 'error' });
    } finally {
      setRemoving(false);
      setMemberToRemove(null);
    }
  };

  const handleCancelInvite = async (inviteId, email) => {
    if (!window.confirm(`Cancel invitation for ${email}?`)) return;
    try {
      const response = await fetch(
        `${API_URL}/workspace/${workspaceId}/members/invite/${inviteId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${keycloak.token}` },
        }
      );
      const result = await response.json();
      if (response.ok && result.success) {
        setToast({ show: true, message: 'Invitation cancelled successfully', type: 'success' });
        fetchMembers();
      } else {
        setToast({
          show: true,
          message: result.message || 'Failed to cancel invitation',
          type: 'error',
        });
      }
    } catch {
      setToast({ show: true, message: 'Failed to cancel invitation', type: 'error' });
    }
  };

  // ——————————————————————————————————————————————————————————————————————————
  const formatRole = (role) => {
    if (role === 'organization_admin' || role === 'org_admin') return 'Admin';
    if (role === 'organization_user' || role === 'org_member') return 'Member';
    if (role === 'organization_owner' || role === 'org_owner') return 'Owner';
    return role;
  };

  const getMemberStatus = (member) => {
    if (member.membership_status === 'pending_invite') return 'invited';
    if (member.kc_enabled && member.email_verified) return 'active';
    if (member.kc_enabled && !member.email_verified) return 'pending';
    return 'inactive';
  };

  const getInitials = (name) => {
    if (!name || name === 'Unknown') return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // ——————————————————————————————————————————————————————————————————————————
  const filteredMembers = members.filter((m) => {
    const name = m.display_name || m.email || '';
    const matchSearch =
      searchTerm === '' ||
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (m.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchRole = roleFilter === 'all' || m.workspace_role === roleFilter;
    const status = getMemberStatus(m);
    const matchStatus = statusFilter === 'all' || status === statusFilter;
    return matchSearch && matchRole && matchStatus;
  });

  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage) || 1;
  const paginatedMembers = filteredMembers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const isActionable = useCallback(
    (m) => {
      if (m.is_owner) return false;
      if (m.user_id === currentUserId) return false;
      if (isSuperadmin || isOwner) return true;
      if (isOrganizationAdmin) return m.workspace_role === 'org_member';
      return false;
    },
    [currentUserId, isSuperadmin, isOwner, isOrganizationAdmin]
  );

  // ——————————————————————————————————————————————————————————————————————————
  if (!canManageMembers) {
    return (
      <div className="tm-page">
        <div className="tm-access-denied">
          <i className="fas fa-lock"></i>
          <h3>Access Restricted</h3>
          <p>Only workspace admins can manage members.</p>
        </div>
      </div>
    );
  }

  const totalSeats = members.length;
  const seatLimit = primaryWorkspace?.seatLimit ?? null; // null = unlimited
  const adminCount = members.filter(
    (m) =>
      (m.workspace_role === 'org_admin' || m.workspace_role === 'organization_admin') && !m.is_owner
  ).length;
  const ownerCount = members.filter((m) => m.is_owner).length;
  const memberCount = members.filter(
    (m) =>
      (m.workspace_role === 'org_member' || m.workspace_role === 'organization_user') && !m.is_owner
  ).length;

  return (
    <div className="tm-page">
      {/* Page Title / Top banner */}
      <Banner title="Team Management" type="page-header">
        <div className="tm-credit-info">
          You are managing a&nbsp;
          <span className="tm-account-type-badge">
            <i className="fas fa-building" style={{ marginRight: '4px' }}></i> Workspace
          </span>
          &nbsp;team &nbsp;&nbsp;·&nbsp;&nbsp;
          <strong>{totalSeats}</strong>
          {seatLimit !== null && (
            <>
              {' '}
              / <strong>{seatLimit}</strong>
            </>
          )}
          &nbsp;seat{totalSeats > 1 ? 's' : ''} used &nbsp;&nbsp;({ownerCount} owner
          {ownerCount > 1 ? 's' : ''}, &nbsp;{adminCount} admin{adminCount > 1 ? 's' : ''}, &nbsp;
          {memberCount} member{memberCount > 1 ? 's' : ''})
        </div>
      </Banner>

      {/* Section Tab Bar */}
      <div className="tm-section-tabs">
        <button
          className={`tm-section-tab ${section === 'team' ? 'active' : ''}`}
          onClick={() => setSection('team')}
        >
          <i className="fas fa-users"></i> All User
        </button>
        <div className="tm-tab-divider"></div>
        <button
          className={`tm-section-tab ${section === 'member-status' ? 'active' : ''}`}
          onClick={() => {
            setSection('member-status');
            fetchCreditMembers();
          }}
        >
          <i className="fas fa-chart-bar"></i> Member Status
        </button>
        <div className="tm-tab-divider"></div>
        <button
          className={`tm-section-tab ${section === 'activity-logs' ? 'active' : ''}`}
          onClick={() => setSection('activity-logs')}
        >
          <i className="fas fa-history"></i> Activity Logs
        </button>
      </div>
      <div className="tm-horizontal-divider"></div>

      {/* Activity Logs Section */}
      {section === 'activity-logs' && (
        <div className="al-embedded">
          <ActivityLog embedded={true} />
        </div>
      )}

      {/* Team Management Section */}
      {section === 'team' && (
        <>
          {/* Alerts */}
          {toast.show && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast({ ...toast, show: false })}
            />
          )}
          {/* Signup link banner — shown when email can't be sent (no SMTP configured) */}
          {signupLink && (
            <Banner
              type="info"
              onClose={() => setSignupLink(null)}
              className="tm-invite-link-banner"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                <strong>Share this invite link directly with the invitee:</strong>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                  <input
                    readOnly
                    value={signupLink}
                    onClick={(e) => e.target.select()}
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #ccc',
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      background: '#f8f9fa',
                      cursor: 'text',
                    }}
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(signupLink);
                    }}
                    className="tm-btn tm-btn-sm"
                    title="Copy link"
                  >
                    <i className="fas fa-copy"></i> Copy
                  </button>
                </div>
              </div>
            </Banner>
          )}

          {/* Filter Bar */}
          <div className="tm-filter-bar">
            <div className="tm-search">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search for member..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="tm-search-clear" onClick={() => setSearchTerm('')}>
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
            <select
              className="tm-select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">All roles</option>
              <option value="org_admin">Admin</option>
              <option value="org_member">Member</option>
            </select>
            <select
              className="tm-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="inactive">Inactive</option>
            </select>
            <button
              className="tm-invite-btn"
              onClick={() => {
                setInviteError(null);
                setShowInviteModal(true);
              }}
            >
              <i className="fas fa-plus"></i> Invite member
            </button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="members-loading-container">
              <LeadgenLoading message="Loading members..." />
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="tm-empty">
              <i className="fas fa-user-friends"></i>
              <p>
                {searchTerm || roleFilter !== 'all' || statusFilter !== 'all'
                  ? 'No members match your filters.'
                  : 'No members yet. Invite team members to start collaborating.'}
              </p>
            </div>
          ) : (
            <Pagination
              columns={[
                { key: 'member', label: 'Member' },
                { key: 'role', label: 'Role' },
                { key: 'status', label: 'Status' },
                { key: 'actions', label: '', width: '44px' },
              ]}
              data={paginatedMembers}
              renderRow={(member) => {
                const displayName =
                  member.username ||
                  member.display_name ||
                  (member.first_name && member.last_name
                    ? `${member.first_name} ${member.last_name}`
                    : member.email || 'Unknown');
                const status = getMemberStatus(member);
                const isSelected = false; // Selection removed
                const isAdmin = member.workspace_role === 'org_admin';

                return (
                  <tr key={member.membership_id} className={isSelected ? 'row-sel' : ''}>
                    <td className="col-member">
                      <div className="tm-member-cell">
                        <div
                          className="tm-avatar"
                          style={{ background: isAdmin ? '#2563eb' : '#3b82f6' }}
                        >
                          {getInitials(displayName)}
                        </div>
                        <div className="tm-member-info">
                          <span className="tm-name" style={{ textTransform: 'lowercase' }}>
                            {displayName}
                          </span>
                          <span className="tm-email">{member.email || '—'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="col-role">
                      {isActionable(member) ? (
                        <select
                          className={`tm-role-select tm-role-select-${member.workspace_role}`}
                          value={member.workspace_role}
                          onChange={(e) => requestRoleChange(member, e.target.value)}
                        >
                          <option value="org_admin">Admin</option>
                          <option value="org_member">Member</option>
                        </select>
                      ) : (
                        <span
                          className={`tm-role-pill tm-role-${member.is_owner ? 'owner' : (member.workspace_role || '').replace('organization_', 'org_')}`}
                        >
                          {member.is_owner ? 'Owner' : formatRole(member.workspace_role)}
                        </span>
                      )}
                    </td>
                    <td className="col-credit">
                      <span className={`tm-credit-badge tm-credit-${status}`}>
                        {status === 'active'
                          ? 'Active'
                          : status === 'pending'
                            ? 'Pending'
                            : status === 'invited'
                              ? 'Invited'
                              : 'Inactive'}
                      </span>
                    </td>
                    <td className="col-act">
                      {isOrganizationAdmin && isActionable(member) && (
                        <button
                          className="tm-remove-btn"
                          title={
                            member.membership_status === 'pending_invite'
                              ? 'Cancel invitation'
                              : 'Remove member'
                          }
                          onClick={() => {
                            if (member.membership_status === 'pending_invite') {
                              handleCancelInvite(member.membership_id, member.email);
                            } else {
                              handleRemove(member);
                            }
                          }}
                        >
                          <i className="fas fa-trash-alt" style={{ color: '#ef4444' }}></i>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              }}
              loading={loading}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(limit) => {
                setCurrentPage(1);
                setItemsPerPage(limit);
              }}
              totalItems={filteredMembers.length}
              itemsName="members"
              className="tm-pagination-card"
            />
          )}

          {/* Invite Modal */}
          {showInviteModal && (
            <div
              className="tm-overlay"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowInviteModal(false);
                  setInviteError(null);
                }
              }}
            >
              <div className="tm-modal">
                <div className="tm-modal-head">
                  <h3>
                    <i className="fas fa-user-plus"></i> Invite a new member
                  </h3>
                  <button
                    className="tm-modal-close"
                    onClick={() => {
                      setShowInviteModal(false);
                      setInviteError(null);
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <form onSubmit={handleInvite}>
                  <div className="tm-modal-body">
                    {inviteError && (
                      <div
                        className="tm-alert tm-alert-danger"
                        style={{
                          marginBottom: '16px',
                          fontSize: '14px',
                          borderLeft: '4px solid #ef4444',
                          background: '#fef2f2',
                          padding: '10px 14px',
                          color: '#b91c1c',
                          borderRadius: '4px',
                          textAlign: 'left',
                        }}
                      >
                        <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                        {inviteError}
                      </div>
                    )}
                    <div className="tm-field">
                      <label style={{ display: 'flex', alignItems: 'center' }}>
                        Email Address
                        <span
                          className="tm-info-trigger"
                          data-tooltip="Only unregistered users on the platform can be invited. They will receive an email notification."
                        >
                          <i
                            className="fas fa-info-circle"
                            style={{
                              marginLeft: '6px',
                              color: '#3b82f6',
                              cursor: 'help',
                              fontSize: '13px',
                            }}
                          ></i>
                        </span>
                      </label>
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="name@company.com"
                        required
                        disabled={inviting}
                        autoFocus
                      />
                    </div>
                    <div className="tm-field">
                      <label style={{ display: 'flex', alignItems: 'center' }}>
                        Role
                        <span
                          className="tm-info-trigger"
                          data-tooltip={
                            'Admin: Full workspace access.\nMember: Only Platform features access.'
                          }
                        >
                          <i
                            className="fas fa-info-circle"
                            style={{
                              marginLeft: '6px',
                              color: '#3b82f6',
                              cursor: 'help',
                              fontSize: '13px',
                            }}
                          ></i>
                        </span>
                      </label>
                      <select
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value)}
                        disabled={inviting}
                      >
                        <option value="org_member">Member</option>
                        {(isSuperadmin || isOwner) && <option value="org_admin">Admin</option>}
                      </select>
                    </div>
                  </div>
                  <div className="tm-modal-foot">
                    <button
                      type="button"
                      className="tm-btn tm-btn-outline"
                      onClick={() => {
                        setShowInviteModal(false);
                        setInviteError(null);
                      }}
                      disabled={inviting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="tm-btn tm-btn-primary"
                      disabled={inviting || !inviteEmail.trim()}
                    >
                      {inviting ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Sending...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane"></i> Send Invite
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {memberToRemove && (
            <div
              className="tm-overlay"
              onClick={(e) => e.target === e.currentTarget && !removing && setMemberToRemove(null)}
            >
              <div className="tm-modal tm-modal-danger" style={{ maxWidth: '450px' }}>
                <div className="tm-modal-head" style={{ borderBottom: 'none', paddingBottom: '0' }}>
                  <h3 style={{ color: '#ef4444' }}>
                    <i className="fas fa-exclamation-triangle"></i> Remove Member
                  </h3>
                  <button
                    className="tm-modal-close"
                    onClick={() => !removing && setMemberToRemove(null)}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="tm-modal-body" style={{ textAlign: 'center', paddingTop: '10px' }}>
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      background: '#fee2e2',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px auto',
                      color: '#ef4444',
                      fontSize: '24px',
                    }}
                  >
                    <i className="fas fa-user-times"></i>
                  </div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1e293b' }}>
                    Remove{' '}
                    {(
                      memberToRemove.username ||
                      memberToRemove.display_name ||
                      memberToRemove.email ||
                      ''
                    ).toLowerCase()}
                    ?
                  </h4>
                  <p
                    style={{
                      margin: '0 0 16px 0',
                      color: '#64748b',
                      fontSize: '14px',
                      lineHeight: '1.5',
                    }}
                  >
                    This action cannot be undone. Their account will be{' '}
                    <strong>permanently deleted</strong> from the platform.
                  </p>

                  <div
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '12px',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <div
                      style={{
                        background: '#e0e7ff',
                        color: '#4f46e5',
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <i className="fas fa-coins"></i>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#64748b',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Credits to return
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a' }}>
                        {memberToRemove.credit_balance || 0} credits
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="tm-modal-foot"
                  style={{
                    background: '#f8fafc',
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex',
                    gap: '12px',
                  }}
                >
                  <button
                    type="button"
                    className="tm-btn tm-btn-outline"
                    onClick={() => setMemberToRemove(null)}
                    disabled={removing}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="tm-btn"
                    onClick={confirmRemoveUser}
                    disabled={removing}
                    style={{
                      flex: 1,
                      background: '#ef4444',
                      color: 'white',
                      borderColor: '#ef4444',
                    }}
                  >
                    {removing ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i> Removing...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-trash-alt"></i> Yes, Remove Member
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {roleChangeConfirm && (
            <div
              className="tm-overlay"
              onClick={(e) => e.target === e.currentTarget && handleCancelRoleChange()}
            >
              <div className="tm-modal tm-modal-danger" style={{ maxWidth: '450px' }}>
                <div className="tm-modal-head" style={{ borderBottom: 'none', paddingBottom: '0' }}>
                  <h3 style={{ color: '#f59e0b' }}>
                    <i className="fas fa-user-shield"></i> Confirm Role Change
                  </h3>
                  <button className="tm-modal-close" onClick={handleCancelRoleChange}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="tm-modal-body" style={{ textAlign: 'center', paddingTop: '10px' }}>
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      background: '#fef3c7',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px auto',
                      color: '#f59e0b',
                      fontSize: '24px',
                    }}
                  >
                    <i className="fas fa-coins"></i>
                  </div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1e293b' }}>
                    Change {roleChangeConfirm.userName} from {roleChangeConfirm.currentRoleLabel} to{' '}
                    {roleChangeConfirm.newRoleLabel}?
                  </h4>
                  <p
                    style={{
                      margin: '0 0 16px 0',
                      color: '#64748b',
                      fontSize: '14px',
                      lineHeight: '1.5',
                    }}
                  >
                    {roleChangeConfirm.newRoleLabel === 'Admin'
                      ? 'Promoting to Admin will return their remaining credit balance to the shared org pool. They will no longer hold personal credits.'
                      : 'Downgrading to Member will return their remaining credit balance to the shared org pool and reset their personal balance to 0.'}
                  </p>

                  {roleChangeConfirm.memberCredit > 0 && (
                    <div
                      style={{
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '12px',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      <div
                        style={{
                          background: '#e0e7ff',
                          color: '#4f46e5',
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <i className="fas fa-coins"></i>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Credits to return to org pool
                        </div>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a' }}>
                          {roleChangeConfirm.memberCredit} credits
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div
                  className="tm-modal-foot"
                  style={{
                    background: '#f8fafc',
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex',
                    gap: '12px',
                  }}
                >
                  <button
                    type="button"
                    className="tm-btn tm-btn-outline"
                    onClick={handleCancelRoleChange}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="tm-btn"
                    onClick={handleConfirmRoleChange}
                    style={{
                      flex: 1,
                      background: '#f59e0b',
                      color: 'white',
                      borderColor: '#f59e0b',
                    }}
                  >
                    <i className="fas fa-check"></i> Confirm
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Member Status Section */}
      {section === 'member-status' && (
        <>
          {toast.show && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast({ ...toast, show: false })}
            />
          )}
          <div className="credit-table-card">
            {creditLoading ? (
              <div className="loading-row">
                <div className="spinner"></div>
                <p>Loading member credit status...</p>
              </div>
            ) : (
              <>
                <table className="credit-table">
                  <thead>
                    <tr>
                      <th>Member</th>
                      <th>Role</th>
                      <th>Credit Balance</th>
                      <th>Usage</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditMembers.map((member) => (
                      <tr key={member.user_id}>
                        <td>
                          <div className="user-cell">
                            <div className="user-avatar">
                              {String(member.display_name || '?')
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                            <div>
                              <div className="user-name-bold">
                                {member.display_name ? String(member.display_name) : 'Unknown Member'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span style={{ textTransform: 'capitalize' }}>
                            {member.role.replace('org_', '')}
                          </span>
                        </td>
                        <td>
                          {member.role === 'org_owner' ||
                          member.role === 'org_admin' ||
                          member.role === 'owner' ||
                          member.role === 'admin' ? (
                            <strong>Owner/Admin</strong>
                          ) : (
                            <strong>{member.credit_balance.toLocaleString()}</strong>
                          )}
                        </td>
                        <td style={{ width: '150px' }}>
                          <div className="status-progress-container">
                            <div
                              className={`status-progress-bar ${member.status}`}
                              style={{ width: `${member.usage_pct}%` }}
                            ></div>
                          </div>
                          <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                            {member.usage_pct}% used
                          </div>
                        </td>
                        <td>
                          {member.role === 'org_owner' ||
                          member.role === 'org_admin' ||
                          member.role === 'owner' ||
                          member.role === 'admin' ? (
                            <span style={{ color: '#999', fontSize: '12px' }}>—</span>
                          ) : (
                            <button
                              className="page-btn"
                              onClick={() => {
                                navigate(
                                  `/credit-management?tab=assign&search=${encodeURIComponent(member.display_name || '')}`
                                );
                              }}
                            >
                              Assign Credits
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {creditMembers.length === 0 && !creditLoading && (
                  <div className="empty-state">
                    <i className="fas fa-users"></i>
                    <p>No members found in this organization.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Members;
